import { useEffect, useState } from 'react';
import { telemetryService } from '../services/telemetryService';

// Performance marks for shell and route timing
export const PerformanceMarks = {
  SHELL_START: 'shell-start',
  SHELL_VISIBLE: 'shell-visible',
  ROUTE_START: 'route-start',
  ROUTE_VISIBLE: 'route-visible',
  ROUTE_INTERACTIVE: 'route-interactive',
  ROUTE_CPU_SETTLED: 'route-cpu-settled',
  BOOTSTRAP_START: 'bootstrap-start',
  BOOTSTRAP_END: 'bootstrap-end',
} as const;

// Mark performance timing
export function markPerformance(mark: string) {
  if (typeof performance !== 'undefined') {
    performance.mark(mark);
  }
}

// Measure between two marks
export function measurePerformance(name: string, startMark: string, endMark: string) {
  if (typeof performance !== 'undefined') {
    try {
      performance.measure(name, startMark, endMark);
      const entries = performance.getEntriesByName(name, 'measure');
      if (entries.length > 0) {
        const duration = entries[entries.length - 1].duration;
        return duration;
      }
    } catch (e) {
      // Mark might not exist, ignore
    }
  }
  return null;
}

// Get all performance measures
export function getPerformanceMeasures() {
  if (typeof performance !== 'undefined') {
    const measures = performance.getEntriesByType('measure');
    return measures.map(m => ({
      name: m.name,
      duration: m.duration,
      startTime: m.startTime,
    }));
  }
  return [];
}

// Clear performance marks and measures
export function clearPerformanceMarks() {
  if (typeof performance !== 'undefined') {
    performance.clearMarks();
    performance.clearMeasures();
  }
}

// Web Vitals collection
type MetricValue = {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  category?: 'web_vital' | 'network' | 'render';
  metadata?: Record<string, unknown>;
};

type MetricCallback = (metric: MetricValue) => void;

// Store collected metrics
const collectedMetrics: Map<string, MetricValue> = new Map();
let metricCallbacks: MetricCallback[] = [];
let webVitalsInitialized = false;

type NetworkDomain =
  | 'other'
  | 'profile'
  | 'usage'
  | 'search'
  | 'scan_history'
  | 'scan_lists'
  | 'health_state'
  | 'labs'
  | 'goals'
  | 'score'
  | 'score_history'
  | 'privacy_consent'
  | 'workspace_meta'
  | 'automation_settings'
  | 'sync_state'
  | 'environment'
  | 'cycle_checkins'
  | 'cycle_periods'
  | 'protocols'
  | 'protocol_library'
  | 'supplements'
  | 'habits'
  | 'playbooks'
  | 'referrals'
  | 'family_overview'
  | 'achievements'
  | 'integrations_snapshot'
  | 'integrations_freshness'
  | 'integrations_detail';

type NetworkCounters = {
  requests: number;
  duplicates: number;
};

type NetworkSnapshot = {
  requests: number;
  duplicates: number;
  perDomain: Record<string, NetworkCounters>;
  perNormalizedUrl: Record<string, number>;
};

export type RouteDeepSectionMount = {
  section: string;
  atMs: number;
  beforeInteractive: boolean;
};

export type ExpensiveSectionReport = {
  section: string;
  atMs: number;
  beforeInteractive: boolean;
};

export type RuntimePlatform = 'web' | 'ios' | 'android';
export type RuntimeAppState = 'foreground' | 'background' | 'background-resume';
export type RuntimeNetworkQuality = 'offline' | 'slow-2g' | '2g' | '3g' | '4g' | 'unknown';
export type RouteFamily = 'shell' | 'chat' | 'health' | 'protocols' | 'scan' | 'family' | 'cycle';
export type StartupContext = 'startup' | 'settled';

export type RuntimeContext = {
  platform: RuntimePlatform;
  appState: RuntimeAppState;
  networkQuality: RuntimeNetworkQuality;
  startupMode: boolean;
  startupPhase: StartupPhase;
};

export type RouteReport = {
  routeName: string;
  routeFamily: RouteFamily;
  recordedAt: string;
  tabSelectMs: number | null;
  routeShellVisibleMs: number | null;
  firstVisibleMs: number | null;
  firstInteractiveMs: number | null;
  routeCpuSettledMs: number | null;
  startupContext: StartupContext;
  startupMode: boolean;
  startupPhase: StartupPhase;
  requestCount: number;
  duplicateRequestCount: number;
  platform: RuntimePlatform;
  appState: RuntimeAppState;
  networkQuality: RuntimeNetworkQuality;
  workspaceId?: string | null;
  domainOwner?: string | null;
  freshnessState?: string | null;
  dominantDomains: Array<{
    domain: string;
    requests: number;
    duplicates: number;
  }>;
  deepSectionMounts: RouteDeepSectionMount[];
  expensiveSections: ExpensiveSectionReport[];
  perDomain: Record<string, NetworkCounters>;
  duplicateUrls?: Array<{
    url: string;
    requests: number;
  }>;
};

export type RouteFamilyMatrixRow = {
  routeFamily: RouteFamily;
  routeName: string;
  startupContext: StartupContext;
  startupPhase: StartupPhase;
  routeShellVisibleMs: number | null;
  firstInteractiveMs: number | null;
  routeCpuSettledMs: number | null;
  requestCount: number;
  duplicateRequestCount: number;
  deepSectionsBeforeInteractive: number;
  dominantDomains: Array<{
    domain: string;
    requests: number;
    duplicates: number;
  }>;
  expensiveSections: ExpensiveSectionReport[];
};

export type PerformanceSnapshot = {
  recordedAt: string;
  runtime: RuntimeContext;
  metrics: Record<string, MetricValue>;
  network: ReturnType<typeof getNetworkSummary>;
  measures: ReturnType<typeof getPerformanceMeasures>;
  routeReports: RouteReport[];
  routeFamilyMatrix: RouteFamilyMatrixRow[];
  startupTrace: StartupTraceReport | null;
};

export type PerformanceDebugApi = {
  logSummary: typeof logPerformanceSummary;
  getRouteReports: typeof getRouteReports;
  getLatestRouteReport: typeof getLatestRouteReport;
  logLatestRouteReport: typeof logLatestRouteReport;
  getRouteFamilyMatrix: typeof getRouteFamilyMatrix;
  exportSnapshot: typeof exportPerformanceSnapshot;
  getStartupTrace: typeof getStartupTrace;
  logStartupTrace: typeof logStartupTrace;
};

export type StartupPhase =
  | 'shell'
  | 'deferred-orchestrator'
  | 'route-hydration'
  | 'background-enrichment'
  | 'settled';

type StartupDomainCounts = Record<string, number>;

export type StartupTraceReport = {
  startedAt: string;
  initialRoute: string | null;
  currentRoute: string | null;
  startupMode: boolean;
  startupPhase: StartupPhase;
  shellFirstVisibleMs: number | null;
  firstRouteInteractiveMs: number | null;
  timeToNetworkQuietMs: number | null;
  timeToCpuQuietMs: number | null;
  cpuQuietSupported: boolean;
  totalStartupRequests: number;
  duplicateStartupRequests: number;
  startupDomainCounts: StartupDomainCounts;
  routeChanges: Array<{
    routeName: string;
    atMs: number;
  }>;
  settledAtMs: number | null;
};

const networkSummary = {
  requests: 0,
  duplicates: 0,
  perNormalizedUrl: new Map<string, number>(),
  perDomain: new Map<NetworkDomain, NetworkCounters>(),
  normalizedUrlDomains: new Map<string, NetworkDomain>()
};
const routeReports: RouteReport[] = [];
const runtimeContext: RuntimeContext = {
  platform: 'web',
  appState: 'foreground',
  networkQuality: 'unknown',
  startupMode: false,
  startupPhase: 'settled'
};

const runtimeContextListeners = new Set<(context: RuntimeContext) => void>();
const startupTraceListeners = new Set<(trace: StartupTraceReport | null) => void>();
const STARTUP_TRACE_MAX_MS = 90_000;
const STARTUP_QUIET_WINDOW_MS = 4_000;
const STARTUP_PHASE_ORDER: Record<StartupPhase, number> = {
  shell: 0,
  'deferred-orchestrator': 1,
  'route-hydration': 2,
  'background-enrichment': 3,
  settled: 4
};

let startupTrace: StartupTraceReport | null = null;
let startupTraceTimeoutId: ReturnType<typeof setTimeout> | null = null;
let startupNetworkQuietTimeoutId: ReturnType<typeof setTimeout> | null = null;
let startupCpuQuietTimeoutId: ReturnType<typeof setTimeout> | null = null;
let startupLongTaskObserver: PerformanceObserver | null = null;
let startupInFlightRequests = 0;
let startupRequestBaselineCounts: Map<string, number> | null = null;
const ROUTE_CPU_SETTLE_WINDOW_MS = 450;

type RouteInteractionState = {
  startedAt: number;
  tabSelectMs: number | null;
  routeShellVisibleMs: number | null;
  routeInteractiveMs: number | null;
  routeCpuSettledMs: number | null;
  deepSectionMounts: RouteDeepSectionMount[];
  cpuSettledTimeoutId: ReturnType<typeof setTimeout> | null;
};

export function inferRouteFamily(routeName: string | null | undefined): RouteFamily {
  switch (routeName) {
    case 'chat':
    case 'today':
      return 'chat';
    case 'health':
    case 'vitals':
    case 'labs':
    case 'insights':
    case 'community':
    case 'progress':
      return 'health';
    case 'protocols':
    case 'calendar':
    case 'plan':
      return 'protocols';
    case 'scan':
      return 'scan';
    case 'family':
      return 'family';
    case 'cycle':
      return 'cycle';
    default:
      return 'shell';
  }
}

const routeInteractionStates = new Map<string, RouteInteractionState>();

const getElapsedStartupMs = () => {
  if (!startupTrace) {
    return 0;
  }
  return Date.now() - Date.parse(startupTrace.startedAt);
};

const emitStartupTrace = () => {
  const snapshot = startupTrace ? {
    ...startupTrace,
    routeChanges: [...startupTrace.routeChanges],
    startupDomainCounts: { ...startupTrace.startupDomainCounts }
  } : null;
  startupTraceListeners.forEach((listener) => listener(snapshot));
};

const clearStartupTimers = () => {
  if (startupTraceTimeoutId !== null) {
    clearTimeout(startupTraceTimeoutId);
    startupTraceTimeoutId = null;
  }
  if (startupNetworkQuietTimeoutId !== null) {
    clearTimeout(startupNetworkQuietTimeoutId);
    startupNetworkQuietTimeoutId = null;
  }
  if (startupCpuQuietTimeoutId !== null) {
    clearTimeout(startupCpuQuietTimeoutId);
    startupCpuQuietTimeoutId = null;
  }
};

const setStartupPhaseInternal = (nextPhase: StartupPhase) => {
  if (!startupTrace || !startupTrace.startupMode) {
    return;
  }
  const currentRank = STARTUP_PHASE_ORDER[startupTrace.startupPhase];
  const nextRank = STARTUP_PHASE_ORDER[nextPhase];
  if (nextRank <= currentRank) {
    return;
  }
  startupTrace = {
    ...startupTrace,
    startupPhase: nextPhase
  };
  setPerformanceRuntimeContext({
    startupMode: true,
    startupPhase: nextPhase
  });
  emitStartupTrace();
};

const finalizeStartupTraceIfSettled = () => {
  if (!startupTrace || !startupTrace.startupMode) {
    return;
  }

  const elapsedMs = getElapsedStartupMs();
  const hasSettledSignals = startupTrace.firstRouteInteractiveMs !== null
    && startupTrace.timeToNetworkQuietMs !== null
    && (startupTrace.cpuQuietSupported === false || startupTrace.timeToCpuQuietMs !== null);

  if (!hasSettledSignals && elapsedMs < STARTUP_TRACE_MAX_MS) {
    return;
  }

  startupTrace = {
    ...startupTrace,
    startupMode: false,
    startupPhase: 'settled',
    settledAtMs: startupTrace.settledAtMs ?? elapsedMs
  };
  setPerformanceRuntimeContext({
    startupMode: false,
    startupPhase: 'settled'
  });
  emitStartupTrace();
};

const scheduleStartupTraceTimeout = () => {
  if (!startupTrace || !startupTrace.startupMode) {
    return;
  }
  if (startupTraceTimeoutId !== null) {
    clearTimeout(startupTraceTimeoutId);
  }
  const remainingMs = Math.max(0, STARTUP_TRACE_MAX_MS - getElapsedStartupMs());
  startupTraceTimeoutId = setTimeout(() => {
    finalizeStartupTraceIfSettled();
  }, remainingMs);
};

const scheduleNetworkQuietCheck = () => {
  if (!startupTrace || !startupTrace.startupMode) {
    return;
  }
  if (startupInFlightRequests > 0) {
    return;
  }
  if (startupNetworkQuietTimeoutId !== null) {
    clearTimeout(startupNetworkQuietTimeoutId);
  }
  startupNetworkQuietTimeoutId = setTimeout(() => {
    if (!startupTrace || !startupTrace.startupMode || startupTrace.timeToNetworkQuietMs !== null) {
      return;
    }
    startupTrace = {
      ...startupTrace,
      timeToNetworkQuietMs: getElapsedStartupMs()
    };
    emitStartupTrace();
    finalizeStartupTraceIfSettled();
  }, STARTUP_QUIET_WINDOW_MS);
};

const scheduleCpuQuietCheck = () => {
  if (!startupTrace || !startupTrace.startupMode) {
    return;
  }
  if (startupCpuQuietTimeoutId !== null) {
    clearTimeout(startupCpuQuietTimeoutId);
  }
  startupCpuQuietTimeoutId = setTimeout(() => {
    if (!startupTrace || !startupTrace.startupMode || startupTrace.timeToCpuQuietMs !== null) {
      return;
    }
    startupTrace = {
      ...startupTrace,
      timeToCpuQuietMs: getElapsedStartupMs()
    };
    emitStartupTrace();
    finalizeStartupTraceIfSettled();
  }, STARTUP_QUIET_WINDOW_MS);
};

const installStartupLongTaskObserver = () => {
  if (typeof window === 'undefined' || typeof PerformanceObserver === 'undefined') {
    scheduleCpuQuietCheck();
    return;
  }

  const supportedEntryTypes = Array.isArray(PerformanceObserver.supportedEntryTypes)
    ? PerformanceObserver.supportedEntryTypes
    : [];

  if (!supportedEntryTypes.includes('longtask')) {
    if (startupTrace) {
      startupTrace = {
        ...startupTrace,
        cpuQuietSupported: false
      };
      emitStartupTrace();
    }
    scheduleCpuQuietCheck();
    return;
  }

  try {
    startupLongTaskObserver?.disconnect();
    startupLongTaskObserver = new PerformanceObserver(() => {
      if (!startupTrace || !startupTrace.startupMode) {
        return;
      }
      if (startupTrace.timeToCpuQuietMs !== null) {
        startupTrace = {
          ...startupTrace,
          timeToCpuQuietMs: null
        };
        emitStartupTrace();
      }
      scheduleCpuQuietCheck();
    });
    startupLongTaskObserver.observe({ type: 'longtask', buffered: true });
    scheduleCpuQuietCheck();
  } catch {
    scheduleCpuQuietCheck();
  }
};

// Register callback for metrics
export function onMetric(callback: MetricCallback) {
  metricCallbacks.push(callback);
  return () => {
    metricCallbacks = metricCallbacks.filter(cb => cb !== callback);
  };
}

// Get collected metrics
export function getCollectedMetrics() {
  return Object.fromEntries(collectedMetrics);
}

export function getRuntimeContext(): RuntimeContext {
  return { ...runtimeContext };
}

export function setPerformanceRuntimeContext(partial: Partial<RuntimeContext>) {
  let changed = false;

  if (partial.platform && runtimeContext.platform !== partial.platform) {
    runtimeContext.platform = partial.platform;
    changed = true;
  }
  if (partial.appState && runtimeContext.appState !== partial.appState) {
    runtimeContext.appState = partial.appState;
    changed = true;
  }
  if (partial.networkQuality && runtimeContext.networkQuality !== partial.networkQuality) {
    runtimeContext.networkQuality = partial.networkQuality;
    changed = true;
  }
  if (typeof partial.startupMode === 'boolean' && runtimeContext.startupMode !== partial.startupMode) {
    runtimeContext.startupMode = partial.startupMode;
    changed = true;
  }
  if (partial.startupPhase && runtimeContext.startupPhase !== partial.startupPhase) {
    runtimeContext.startupPhase = partial.startupPhase;
    changed = true;
  }

  if (changed) {
    runtimeContextListeners.forEach((listener) => listener(getRuntimeContext()));
  }
}

export function onRuntimeContextChange(callback: (context: RuntimeContext) => void) {
  runtimeContextListeners.add(callback);
  return () => {
    runtimeContextListeners.delete(callback);
  };
}

export function beginStartupTrace(initialRoute: string | null = null) {
  clearStartupTimers();
  startupLongTaskObserver?.disconnect();
  startupInFlightRequests = 0;
  startupRequestBaselineCounts = new Map(networkSummary.perNormalizedUrl);
  startupTrace = {
    startedAt: new Date().toISOString(),
    initialRoute,
    currentRoute: initialRoute,
    startupMode: true,
    startupPhase: 'shell',
    shellFirstVisibleMs: null,
    firstRouteInteractiveMs: null,
    timeToNetworkQuietMs: null,
    timeToCpuQuietMs: null,
    cpuQuietSupported: true,
    totalStartupRequests: 0,
    duplicateStartupRequests: 0,
    startupDomainCounts: {},
    routeChanges: initialRoute ? [{ routeName: initialRoute, atMs: 0 }] : [],
    settledAtMs: null
  };
  setPerformanceRuntimeContext({
    startupMode: true,
    startupPhase: 'shell'
  });
  emitStartupTrace();
  scheduleStartupTraceTimeout();
  scheduleNetworkQuietCheck();
  installStartupLongTaskObserver();
  return getStartupTrace();
}

export function getStartupTrace() {
  return startupTrace ? {
    ...startupTrace,
    routeChanges: [...startupTrace.routeChanges],
    startupDomainCounts: { ...startupTrace.startupDomainCounts }
  } : null;
}

export function logStartupTrace() {
  const trace = getStartupTrace();
  if (!trace) {
    console.info('[Performance Startup Trace] No startup trace recorded yet.');
    return null;
  }

  console.group(`[Performance Startup Trace] ${trace.currentRoute || trace.initialRoute || 'unknown'}`);
  console.table({
    initialRoute: trace.initialRoute ?? 'n/a',
    currentRoute: trace.currentRoute ?? 'n/a',
    startupMode: trace.startupMode,
    startupPhase: trace.startupPhase,
    shellFirstVisibleMs: trace.shellFirstVisibleMs?.toFixed(2) ?? 'n/a',
    firstRouteInteractiveMs: trace.firstRouteInteractiveMs?.toFixed(2) ?? 'n/a',
    timeToNetworkQuietMs: trace.timeToNetworkQuietMs?.toFixed(2) ?? 'n/a',
    timeToCpuQuietMs: trace.timeToCpuQuietMs?.toFixed(2) ?? 'n/a',
    totalStartupRequests: trace.totalStartupRequests,
    duplicateStartupRequests: trace.duplicateStartupRequests,
    settledAtMs: trace.settledAtMs?.toFixed(2) ?? 'n/a'
  });
  if (Object.keys(trace.startupDomainCounts).length > 0) {
    console.table(trace.startupDomainCounts);
  }
  console.groupEnd();
  return trace;
}

export function onStartupTraceChange(callback: (trace: StartupTraceReport | null) => void) {
  startupTraceListeners.add(callback);
  callback(getStartupTrace());
  return () => {
    startupTraceListeners.delete(callback);
  };
}

export function updateStartupPhase(nextPhase: StartupPhase) {
  setStartupPhaseInternal(nextPhase);
}

export function markStartupRouteChange(routeName: string) {
  if (!startupTrace || !startupTrace.startupMode) {
    return;
  }
  const elapsedMs = getElapsedStartupMs();
  const previousRoute = startupTrace.currentRoute;
  const routeChanges = startupTrace.routeChanges;
  const lastRoute = routeChanges[routeChanges.length - 1]?.routeName;
  startupTrace = {
    ...startupTrace,
    currentRoute: routeName,
    routeChanges: lastRoute === routeName
      ? routeChanges
      : [...routeChanges, { routeName, atMs: elapsedMs }]
  };
  if (previousRoute && previousRoute !== routeName) {
    setStartupPhaseInternal('route-hydration');
  }
  emitStartupTrace();
}

export function useStartupTraceState() {
  const [trace, setTrace] = useState<StartupTraceReport | null>(() => getStartupTrace());

  useEffect(() => onStartupTraceChange(setTrace), []);

  return trace;
}

// Rating thresholds based on web.dev recommendations
const RATING_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  INP: { good: 200, poor: 500 },
  TTFB: { good: 800, poor: 1800 },
  FCP: { good: 1800, poor: 3000 },
} as const;

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = RATING_THRESHOLDS[name as keyof typeof RATING_THRESHOLDS];
  if (!thresholds) return 'needs-improvement';
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.poor) return 'needs-improvement';
  return 'poor';
}

// Collect metric
function collectMetric(
  name: string,
  value: number,
  delta: number = 0,
  category: MetricValue['category'] = 'web_vital',
  metadata?: Record<string, unknown>
) {
  const metric: MetricValue = {
    name,
    value,
    rating: getRating(name, value),
    delta,
    id: `${name}-${Date.now()}`,
    category,
    metadata: {
      ...getRuntimeContext(),
      ...(metadata || {})
    },
  };
  
  const previous = collectedMetrics.get(name);
  if (previous) {
    metric.delta = value - previous.value;
  }
  
  collectedMetrics.set(name, metric);
  metricCallbacks.forEach(cb => cb(metric));
  
  return metric;
}

const normalizeUrlForTracking = (url: string, method: string) => {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const parsed = new URL(url, base);
    const params = new URLSearchParams(parsed.search);
    ['userId', 'sinceRevision', 'ts', '_', 'cacheBust'].forEach((key) => {
      params.delete(key);
    });
    const normalizedSearch = params.toString();
    return `${method.toUpperCase()} ${parsed.pathname}${normalizedSearch ? `?${normalizedSearch}` : ''}`;
  } catch {
    return `${method.toUpperCase()} ${url}`;
  }
};

const isOperationalProbe = (url: string) => {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const pathname = new URL(url, base).pathname;
    return pathname === '/livez' || pathname.endsWith('/livez');
  } catch {
    return false;
  }
};

const inferNetworkDomain = (url: string): NetworkDomain => {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const pathname = new URL(url, base).pathname;

    if (pathname.includes('/api/search/suggestions') || pathname.includes('/api/search')) return 'search';
    if (pathname.includes('/api/scan/history')) return 'scan_history';
    if (pathname.includes('/api/scan/lists')) return 'scan_lists';
    if (pathname.includes('/api/profile')) return 'profile';
    if (pathname.includes('/api/usage')) return 'usage';
    if (pathname.includes('/api/health-state')) return 'health_state';
    if (pathname.includes('/api/labs')) return 'labs';
    if (pathname.includes('/api/goals')) return 'goals';
    if (pathname.includes('/api/score/history')) return 'score_history';
    if (pathname.includes('/api/score')) return 'score';
    if (pathname.includes('/api/privacy/consent')) return 'privacy_consent';
    if (pathname.includes('/api/workspaces/me')) return 'workspace_meta';
    if (pathname.includes('/api/automation/settings')) return 'automation_settings';
    if (pathname.includes('/api/sync/state')) return 'sync_state';
    if (pathname.includes('/api/environment')) return 'environment';
    if (pathname.includes('/api/achievements')) return 'achievements';
    if (pathname.includes('/api/cycle/period-starts')) return 'cycle_periods';
    if (pathname.includes('/api/checkins') || pathname.includes('/api/cycle/check-ins')) return 'cycle_checkins';
    if (pathname.includes('/api/protocols/library')) return 'protocol_library';
    if (pathname.includes('/api/protocols')) return 'protocols';
    if (pathname.includes('/api/supplements')) return 'supplements';
    if (pathname.includes('/api/habits')) return 'habits';
    if (pathname.includes('/api/playbooks')) return 'playbooks';
    if (pathname.includes('/api/referrals')) return 'referrals';
    if (pathname.includes('/api/family/overview')) return 'family_overview';
    if (pathname.includes('/api/calendar/events')) return 'integrations_detail';
    if (pathname.includes('/api/calendar/load')) return 'workspace_meta';
    if (pathname.includes('/api/preventive-care')) return 'health_state';
    if (pathname.includes('/api/contraceptives')) return 'cycle_checkins';
    if (pathname.includes('/api/integrations/snapshot')) return 'integrations_snapshot';
    if (pathname.includes('/api/integrations/sync/freshness')) return 'integrations_freshness';
    if (pathname.includes('/api/integrations')) return 'integrations_detail';
  } catch {
    return 'other';
  }

  return 'other';
};

const ensureDomainCounters = (domain: NetworkDomain) => {
  const existing = networkSummary.perDomain.get(domain);
  if (existing) {
    return existing;
  }
  const next = { requests: 0, duplicates: 0 };
  networkSummary.perDomain.set(domain, next);
  return next;
};

const getNow = () => (
  typeof performance !== 'undefined'
    ? performance.now()
    : Date.now()
);

const cloneDeepSectionMounts = (mounts: RouteDeepSectionMount[] = []) => mounts.map((mount) => ({ ...mount }));

const getExpensiveSections = (mounts: RouteDeepSectionMount[] = []): ExpensiveSectionReport[] => (
  cloneDeepSectionMounts(mounts)
    .sort((left, right) => {
      if (left.beforeInteractive !== right.beforeInteractive) {
        return Number(right.beforeInteractive) - Number(left.beforeInteractive);
      }
      return right.atMs - left.atMs;
    })
    .slice(0, 5)
);

const updateLatestRouteReport = (routeName: string, updater: (report: RouteReport) => RouteReport) => {
  const index = routeReports.findIndex((report) => report.routeName === routeName);
  if (index < 0) {
    return null;
  }
  const nextReport = updater(routeReports[index]);
  routeReports[index] = nextReport;
  return nextReport;
};

const beginRouteInteraction = (routeName: string) => {
  const existing = routeInteractionStates.get(routeName);
  if (existing?.cpuSettledTimeoutId) {
    clearTimeout(existing.cpuSettledTimeoutId);
  }
  routeInteractionStates.set(routeName, {
    startedAt: getNow(),
    tabSelectMs: null,
    routeShellVisibleMs: null,
    routeInteractiveMs: null,
    routeCpuSettledMs: null,
    deepSectionMounts: [],
    cpuSettledTimeoutId: null
  });
};

const getRouteInteractionState = (routeName: string) => routeInteractionStates.get(routeName) ?? null;

const scheduleRouteCpuSettled = (routeName: string) => {
  const state = routeInteractionStates.get(routeName);
  if (!state) {
    return;
  }

  if (state.cpuSettledTimeoutId) {
    clearTimeout(state.cpuSettledTimeoutId);
  }

  state.cpuSettledTimeoutId = setTimeout(() => {
    const latestState = routeInteractionStates.get(routeName);
    if (!latestState || latestState.routeCpuSettledMs != null) {
      return;
    }

    const routeCpuSettledMs = getNow() - latestState.startedAt;
    latestState.routeCpuSettledMs = routeCpuSettledMs;
    latestState.cpuSettledTimeoutId = null;
    markPerformance(`${PerformanceMarks.ROUTE_CPU_SETTLED}-${routeName}`);
    collectMetric(`route-cpu-settled:${routeName}`, routeCpuSettledMs, 0, 'render', {
      routeName,
      deepSectionMounts: cloneDeepSectionMounts(latestState.deepSectionMounts),
      ...getRuntimeContext(),
      ...telemetryService.getContext()
    });
    updateLatestRouteReport(routeName, (report) => ({
      ...report,
      routeCpuSettledMs,
      deepSectionMounts: cloneDeepSectionMounts(latestState.deepSectionMounts)
    }));
  }, ROUTE_CPU_SETTLE_WINDOW_MS);
};

export function markRouteDeepSectionMount(routeName: string, section: string) {
  if (!routeName || !section) {
    return;
  }

  const state = routeInteractionStates.get(routeName);
  if (!state || state.deepSectionMounts.some((mount) => mount.section === section)) {
    return;
  }

  const atMs = getNow() - state.startedAt;
  const mount: RouteDeepSectionMount = {
    section,
    atMs,
    beforeInteractive: state.routeInteractiveMs == null || atMs < state.routeInteractiveMs
  };
  state.deepSectionMounts.push(mount);
  collectMetric(`route-deep-section-mount:${routeName}:${section}`, atMs, 0, 'render', {
    routeName,
    section,
    beforeInteractive: mount.beforeInteractive,
    ...getRuntimeContext(),
    ...telemetryService.getContext()
  });
  updateLatestRouteReport(routeName, (report) => ({
    ...report,
    deepSectionMounts: cloneDeepSectionMounts(state.deepSectionMounts)
  }));
  scheduleRouteCpuSettled(routeName);
}

export function useRouteDeepSectionMount(routeName: string, section: string, enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    markRouteDeepSectionMount(routeName, section);
  }, [enabled, routeName, section]);
}

export function trackNetworkRequest(url: string, method: string = 'GET') {
  if (isOperationalProbe(url)) {
    return () => {};
  }

  const normalizedUrl = normalizeUrlForTracking(url, method);
  const domain = inferNetworkDomain(url);
  const nextCount = (networkSummary.perNormalizedUrl.get(normalizedUrl) || 0) + 1;
  const isDuplicate = nextCount > 1;
  const startupBaselineCount = startupRequestBaselineCounts?.get(normalizedUrl) || 0;
  const isStartupDuplicate = nextCount > (startupBaselineCount + 1);

  networkSummary.perNormalizedUrl.set(normalizedUrl, nextCount);
  networkSummary.normalizedUrlDomains.set(normalizedUrl, domain);
  networkSummary.requests += 1;
  if (isDuplicate) {
    networkSummary.duplicates += 1;
  }

  const counters = ensureDomainCounters(domain);
  counters.requests += 1;
  if (isDuplicate) {
    counters.duplicates += 1;
  }

  if (startupTrace?.startupMode) {
    startupInFlightRequests += 1;
    if (startupNetworkQuietTimeoutId !== null) {
      clearTimeout(startupNetworkQuietTimeoutId);
      startupNetworkQuietTimeoutId = null;
    }
    startupTrace = {
      ...startupTrace,
      timeToNetworkQuietMs: null,
      totalStartupRequests: startupTrace.totalStartupRequests + 1,
      duplicateStartupRequests: startupTrace.duplicateStartupRequests + (isStartupDuplicate ? 1 : 0),
      startupDomainCounts: {
        ...startupTrace.startupDomainCounts,
        [domain]: (startupTrace.startupDomainCounts[domain] || 0) + 1
      }
    };
    emitStartupTrace();
    if (startupTrace.firstRouteInteractiveMs !== null) {
      setStartupPhaseInternal('background-enrichment');
    }
  }

  let completed = false;
  return () => {
    if (completed) {
      return;
    }
    completed = true;
    if (!startupTrace?.startupMode) {
      return;
    }
    startupInFlightRequests = Math.max(0, startupInFlightRequests - 1);
    if (startupInFlightRequests === 0) {
      scheduleNetworkQuietCheck();
      finalizeStartupTraceIfSettled();
    }
  };
}

export function takeNetworkSnapshot(): NetworkSnapshot {
  return {
    requests: networkSummary.requests,
    duplicates: networkSummary.duplicates,
    perDomain: Object.fromEntries(
      Array.from(networkSummary.perDomain.entries()).map(([domain, counters]) => [
        domain,
        { ...counters }
      ])
    ),
    perNormalizedUrl: Object.fromEntries(networkSummary.perNormalizedUrl.entries())
  };
}

export function diffNetworkSnapshot(start: NetworkSnapshot): NetworkSnapshot {
  const perDomainAccumulator = new Map<string, NetworkCounters>();
  const perNormalizedUrl: Record<string, number> = {};
  let windowRequests = 0;
  let windowDuplicates = 0;

  for (const [normalizedUrl, currentCount] of networkSummary.perNormalizedUrl.entries()) {
    const previousCount = start.perNormalizedUrl[normalizedUrl] || 0;
    const delta = Math.max(0, currentCount - previousCount);
    if (delta === 0) {
      continue;
    }

    perNormalizedUrl[normalizedUrl] = delta;

    const domain = networkSummary.normalizedUrlDomains.get(normalizedUrl) || 'other';
    const counters = perDomainAccumulator.get(domain) || { requests: 0, duplicates: 0 };
    counters.requests += delta;
    counters.duplicates += Math.max(0, delta - 1);
    perDomainAccumulator.set(domain, counters);

    windowRequests += delta;
    windowDuplicates += Math.max(0, delta - 1);
  }

  const domains = new Set([
    ...Object.keys(start.perDomain),
    ...Array.from(perDomainAccumulator.keys())
  ]);

  const perDomain = Object.fromEntries(
    Array.from(domains).map((domain) => [
      domain,
      perDomainAccumulator.get(domain) || { requests: 0, duplicates: 0 }
    ])
  );

  return {
    requests: windowRequests,
    duplicates: windowDuplicates,
    perDomain,
    perNormalizedUrl
  };
}

export function getNetworkSummary() {
  return {
    requests: networkSummary.requests,
    duplicates: networkSummary.duplicates,
    perDomain: Object.fromEntries(
      Array.from(networkSummary.perDomain.entries()).map(([domain, counters]) => [
        domain,
        { ...counters }
      ])
    )
  };
}

function getDominantDomains(perDomain: Record<string, NetworkCounters>) {
  return Object.entries(perDomain)
    .map(([domain, counters]) => ({
      domain,
      requests: counters.requests,
      duplicates: counters.duplicates
    }))
    .filter((entry) => entry.requests > 0)
    .sort((left, right) => {
      if (right.requests !== left.requests) {
        return right.requests - left.requests;
      }
      return right.duplicates - left.duplicates;
    })
    .slice(0, 5);
}

function getDuplicateUrls(perNormalizedUrl: Record<string, number>) {
  return Object.entries(perNormalizedUrl)
    .filter(([, count]) => count > 1)
    .sort((left, right) => right[1] - left[1])
    .slice(0, 5)
    .map(([url, requests]) => ({ url, requests }));
}

function recordRouteReport(report: RouteReport) {
  routeReports.unshift(report);
  if (routeReports.length > 25) {
    routeReports.length = 25;
  }
  return report;
}

export function getRouteReports() {
  return [...routeReports];
}

export function getLatestRouteReport(routeName?: string) {
  if (!routeName) {
    return routeReports[0] ?? null;
  }
  return routeReports.find((report) => report.routeName === routeName) ?? null;
}

export function logLatestRouteReport(routeName?: string) {
  const report = getLatestRouteReport(routeName);
  if (!report) {
    console.info('[Performance Route Report] No route report recorded yet.');
    return null;
  }

  console.group(`[Performance Route Report] ${report.routeName}`);
  console.table({
    routeFamily: report.routeFamily,
    firstVisibleMs: report.firstVisibleMs?.toFixed(2) ?? 'n/a',
    firstInteractiveMs: report.firstInteractiveMs?.toFixed(2) ?? 'n/a',
    routeCpuSettledMs: report.routeCpuSettledMs?.toFixed(2) ?? 'n/a',
    startupContext: report.startupContext,
    startupMode: report.startupMode,
    startupPhase: report.startupPhase,
    requestCount: report.requestCount,
    duplicateRequestCount: report.duplicateRequestCount,
    platform: report.platform,
    appState: report.appState,
    networkQuality: report.networkQuality,
    workspaceId: report.workspaceId ?? 'n/a',
    domainOwner: report.domainOwner ?? 'n/a',
    freshnessState: report.freshnessState ?? 'n/a'
  });
  if (report.dominantDomains.length > 0) {
    console.table(report.dominantDomains);
  }
  if (report.expensiveSections.length > 0) {
    console.table(report.expensiveSections);
  }
  if (Array.isArray(report.duplicateUrls) && report.duplicateUrls.length > 0) {
    console.table(report.duplicateUrls);
  }
  console.groupEnd();
  return report;
}

export function getRouteFamilyMatrix(): RouteFamilyMatrixRow[] {
  const latestByFamily = new Map<RouteFamily, RouteReport>();
  for (const report of routeReports) {
    if (!latestByFamily.has(report.routeFamily)) {
      latestByFamily.set(report.routeFamily, report);
    }
  }

  return Array.from(latestByFamily.values()).map((report) => ({
    routeFamily: report.routeFamily,
    routeName: report.routeName,
    startupContext: report.startupContext,
    startupPhase: report.startupPhase,
    routeShellVisibleMs: report.routeShellVisibleMs,
    firstInteractiveMs: report.firstInteractiveMs,
    routeCpuSettledMs: report.routeCpuSettledMs,
    requestCount: report.requestCount,
    duplicateRequestCount: report.duplicateRequestCount,
    deepSectionsBeforeInteractive: report.deepSectionMounts.filter((mount) => mount.beforeInteractive).length,
    dominantDomains: report.dominantDomains,
    expensiveSections: report.expensiveSections
  }));
}

export function exportPerformanceSnapshot(): PerformanceSnapshot {
  return {
    recordedAt: new Date().toISOString(),
    runtime: getRuntimeContext(),
    metrics: getCollectedMetrics(),
    network: getNetworkSummary(),
    measures: getPerformanceMeasures(),
    routeReports: getRouteReports(),
    routeFamilyMatrix: getRouteFamilyMatrix(),
    startupTrace: getStartupTrace()
  };
}

const isPerformanceDebugEnabled = () => {
  if (typeof window === 'undefined') return false;
  const query = new URLSearchParams(window.location.search);
  return (
    query.has('perf') ||
    query.has('devtools') ||
    Boolean((window as Window & { __NOOX_ENABLE_PERF__?: boolean }).__NOOX_ENABLE_PERF__) ||
    Boolean((import.meta as any).env?.DEV)
  );
};

const createPerformanceDebugApi = (): PerformanceDebugApi => ({
  logSummary: logPerformanceSummary,
  getRouteReports,
  getLatestRouteReport,
  logLatestRouteReport,
  getRouteFamilyMatrix,
  exportSnapshot: exportPerformanceSnapshot,
  getStartupTrace,
  logStartupTrace
});

export function installPerformanceDebugBridge() {
  if (typeof window === 'undefined' || !isPerformanceDebugEnabled()) {
    return null;
  }

  const perfWindow = window as Window & { __NOOX_PERF__?: PerformanceDebugApi };
  perfWindow.__NOOX_PERF__ = perfWindow.__NOOX_PERF__ || createPerformanceDebugApi();
  return perfWindow.__NOOX_PERF__;
}

installPerformanceDebugBridge();

// Initialize web vitals collection
export function initWebVitals() {
  if (typeof window === 'undefined' || webVitalsInitialized) return;
  webVitalsInitialized = true;

  // Use PerformanceObserver for Core Web Vitals
  try {
    const supportedEntryTypes = Array.isArray(PerformanceObserver?.supportedEntryTypes)
      ? PerformanceObserver.supportedEntryTypes
      : [];

    // LCP (Largest Contentful Paint)
    if (supportedEntryTypes.includes('largest-contentful-paint')) {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          collectMetric('LCP', lastEntry.startTime);
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    }

    // FID (First Input Delay) - deprecated, use INP
    if (supportedEntryTypes.includes('first-input')) {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (entry.processingStart && entry.startTime) {
            collectMetric('FID', entry.processingStart - entry.startTime);
          }
        });
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    }

    // INP (Interaction to Next Paint)
    let maxINP = 0;
    if (supportedEntryTypes.includes('event')) {
      const inpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (entry.interactionId && entry.duration) {
            maxINP = Math.max(maxINP, entry.duration);
            collectMetric('INP', maxINP);
          }
        });
      });
      inpObserver.observe({ type: 'event', buffered: true });
    }

    // CLS (Cumulative Layout Shift)
    let clsValue = 0;
    if (supportedEntryTypes.includes('layout-shift')) {
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput && entry.value) {
            clsValue += entry.value;
            collectMetric('CLS', clsValue);
          }
        });
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    }

    // FCP (First Contentful Paint)
    if (supportedEntryTypes.includes('paint')) {
      const fcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (entry.name === 'first-contentful-paint') {
            collectMetric('FCP', entry.startTime);
          }
        });
      });
      fcpObserver.observe({ type: 'paint', buffered: true });
    }

    // TTFB (Time to First Byte)
    const navigationEntries = performance.getEntriesByType('navigation');
    if (navigationEntries.length > 0) {
      const navEntry = navigationEntries[0] as PerformanceNavigationTiming;
      if (navEntry.responseStart && navEntry.requestStart) {
        collectMetric('TTFB', navEntry.responseStart - navEntry.requestStart);
      }
    }
  } catch (e) {
    console.warn('[WebVitals] PerformanceObserver not fully supported:', e);
  }
}

// Hook for route-level performance tracking
type RoutePerformanceOptions = {
  interactiveWhen?: boolean;
  fallbackTimeoutMs?: number;
};

export function useRoutePerformance(routeName: string, options: RoutePerformanceOptions = {}) {
  useEffect(() => {
    if (!routeName) return;

    beginRouteInteraction(routeName);
    const networkSnapshot = takeNetworkSnapshot();
    const routeStartMark = `${PerformanceMarks.ROUTE_START}-${routeName}`;
    const routeVisibleMark = `${PerformanceMarks.ROUTE_VISIBLE}-${routeName}`;
    const routeInteractiveMark = `${PerformanceMarks.ROUTE_INTERACTIVE}-${routeName}`;
    const interactiveWhen = options.interactiveWhen ?? true;
    const fallbackTimeoutMs = options.fallbackTimeoutMs ?? 1500;
    let recorded = false;
    let interactiveRafId: number | null = null;
    let fallbackTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const recordRouteMetrics = (markVisible: boolean, markInteractive: boolean) => {
      if (recorded) {
        return;
      }
      recorded = true;

      if (markVisible) {
        markPerformance(routeVisibleMark);
      }
      const firstVisibleMs = measurePerformance(
        `route-render-${routeName}`,
        routeStartMark,
        routeVisibleMark
      );

      if (markInteractive) {
        markPerformance(routeInteractiveMark);
      }
      const firstInteractiveMs = measurePerformance(
        `route-interactive-${routeName}`,
        routeStartMark,
        routeInteractiveMark
      );
      const interactionState = getRouteInteractionState(routeName);
      if (interactionState) {
        interactionState.tabSelectMs = firstVisibleMs;
        interactionState.routeShellVisibleMs = firstVisibleMs;
        interactionState.routeInteractiveMs = firstInteractiveMs;
      }

      const networkDelta = diffNetworkSnapshot(networkSnapshot);
      const runtime = getRuntimeContext();
      const telemetryContext = telemetryService.getContext();
      const routeFamily = inferRouteFamily(routeName);
      const expensiveSections = getExpensiveSections(interactionState?.deepSectionMounts);
      const startupContext: StartupContext = startupTrace?.startupMode ? 'startup' : 'settled';
      const report = recordRouteReport({
        routeName,
        routeFamily,
        recordedAt: new Date().toISOString(),
        tabSelectMs: interactionState?.tabSelectMs ?? firstVisibleMs,
        routeShellVisibleMs: interactionState?.routeShellVisibleMs ?? firstVisibleMs,
        firstVisibleMs,
        firstInteractiveMs,
        routeCpuSettledMs: interactionState?.routeCpuSettledMs ?? null,
        startupContext,
        startupMode: Boolean(startupTrace?.startupMode),
        startupPhase: startupTrace?.startupMode ? startupTrace.startupPhase : 'settled',
        requestCount: networkDelta.requests,
        duplicateRequestCount: networkDelta.duplicates,
        platform: runtime.platform,
        appState: runtime.appState,
        networkQuality: runtime.networkQuality,
        workspaceId: telemetryContext.workspaceId ?? null,
        domainOwner: telemetryContext.domainOwner ?? null,
        freshnessState: telemetryContext.freshnessState ?? null,
        dominantDomains: getDominantDomains(networkDelta.perDomain),
        deepSectionMounts: cloneDeepSectionMounts(interactionState?.deepSectionMounts),
        expensiveSections,
        perDomain: networkDelta.perDomain,
        duplicateUrls: getDuplicateUrls(networkDelta.perNormalizedUrl)
      });

      if (startupTrace?.startupMode) {
        startupTrace = {
          ...startupTrace,
          firstRouteInteractiveMs: startupTrace.firstRouteInteractiveMs ?? firstInteractiveMs
        };
        setStartupPhaseInternal('background-enrichment');
        emitStartupTrace();
        finalizeStartupTraceIfSettled();
      }

      collectMetric(
        `route-request-count:${routeName}`,
        networkDelta.requests,
        0,
        'network',
        {
          routeName,
          perDomain: networkDelta.perDomain,
          dominantDomains: report.dominantDomains,
          routeFamily,
          startupContext,
          platform: runtime.platform,
          appState: runtime.appState,
          networkQuality: runtime.networkQuality,
          workspaceId: telemetryContext.workspaceId ?? null,
          domainOwner: telemetryContext.domainOwner ?? null,
          freshnessState: telemetryContext.freshnessState ?? null
        }
      );
      collectMetric(
        `route-duplicate-request-count:${routeName}`,
        networkDelta.duplicates,
        0,
        'network',
        {
          routeName,
          perDomain: networkDelta.perDomain,
          dominantDomains: report.dominantDomains,
          routeFamily,
          startupContext,
          platform: runtime.platform,
          appState: runtime.appState,
          networkQuality: runtime.networkQuality,
          workspaceId: telemetryContext.workspaceId ?? null,
          domainOwner: telemetryContext.domainOwner ?? null,
          freshnessState: telemetryContext.freshnessState ?? null
        }
      );
      scheduleRouteCpuSettled(routeName);
    };

    markPerformance(routeStartMark);

    // Mark visible after paint
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        markPerformance(routeVisibleMark);

        const markInteractiveWhenReady = () => {
          if (recorded) {
            return;
          }
          if (interactiveWhen) {
            recordRouteMetrics(true, true);
            return;
          }
          interactiveRafId = requestAnimationFrame(markInteractiveWhenReady);
        };

        interactiveRafId = requestAnimationFrame(markInteractiveWhenReady);
      });
    });

    fallbackTimeoutId = setTimeout(() => {
      recordRouteMetrics(true, true);
    }, fallbackTimeoutMs);

    return () => {
      cancelAnimationFrame(rafId);
      if (interactiveRafId !== null) {
        cancelAnimationFrame(interactiveRafId);
      }
      if (fallbackTimeoutId !== null) {
        clearTimeout(fallbackTimeoutId);
      }
    };
  }, [options.fallbackTimeoutMs, options.interactiveWhen, routeName]);
}

// Hook for shell performance tracking
export function useShellPerformance() {
  useEffect(() => {
    beginStartupTrace(
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('view')
        : null
    );
    const networkSnapshot = takeNetworkSnapshot();
    markPerformance(PerformanceMarks.SHELL_START);
    
    // Mark visible after first paint
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        markPerformance(PerformanceMarks.SHELL_VISIBLE);
        const shellRenderMs = measurePerformance(
          'shell-render',
          PerformanceMarks.SHELL_START,
          PerformanceMarks.SHELL_VISIBLE
        );
        if (startupTrace?.startupMode) {
          startupTrace = {
            ...startupTrace,
            shellFirstVisibleMs: shellRenderMs
          };
          emitStartupTrace();
        }
        const networkDelta = diffNetworkSnapshot(networkSnapshot);
        collectMetric(
          'shell-request-count',
          networkDelta.requests,
          0,
          'network',
          { ...getRuntimeContext(), perDomain: networkDelta.perDomain }
        );
        collectMetric(
          'shell-duplicate-request-count',
          networkDelta.duplicates,
          0,
          'network',
          { ...getRuntimeContext(), perDomain: networkDelta.perDomain }
        );
      });
    });

    // Initialize web vitals on shell mount
    initWebVitals();

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, []);
}

// Log performance summary (for debugging)
export function logPerformanceSummary() {
  if (typeof performance === 'undefined') return;

  const measures = getPerformanceMeasures();
  const metrics = getCollectedMetrics();
  const network = getNetworkSummary();
  const latestRouteReports = routeReports.slice(0, 5);
  const runtime = getRuntimeContext();
  const startup = getStartupTrace();
  
  console.group('[Performance Summary]');
  console.log('Runtime Context:', runtime);
  
  if (measures.length > 0) {
    console.log('Custom Measures:');
    measures.forEach(m => {
      console.log(`  ${m.name}: ${m.duration.toFixed(2)}ms`);
    });
  }
  
  if (Object.keys(metrics).length > 0) {
    console.log('Web Vitals:');
    Object.entries(metrics).forEach(([name, metric]: [string, any]) => {
      const emoji = metric.rating === 'good' ? '✅' : metric.rating === 'poor' ? '❌' : '⚠️';
      console.log(`  ${emoji} ${name}: ${metric.value.toFixed(2)} (${metric.rating})`);
    });
  }

  console.log('Network Summary:', network);
  if (startup) {
    console.log('Startup Trace:', startup);
  }

  if (latestRouteReports.length > 0) {
    console.log('Latest Route Reports:');
    console.table(latestRouteReports.map((report) => ({
      routeName: report.routeName,
      firstVisibleMs: report.firstVisibleMs?.toFixed(2) ?? 'n/a',
      firstInteractiveMs: report.firstInteractiveMs?.toFixed(2) ?? 'n/a',
      requestCount: report.requestCount,
      duplicateRequestCount: report.duplicateRequestCount,
      platform: report.platform,
      appState: report.appState,
      networkQuality: report.networkQuality,
      dominantDomains: report.dominantDomains.map((domain) => domain.domain).join(', ')
    })));
  }
  
  console.groupEnd();
}
